import {
  Button,
  EmptyState,
  Icon,
  KeyValue,
  List,
  ListRow,
  MapView,
  MoreMenu,
  PageHeader,
  compactQuery,
  phoneQuery,
  Section,
  Skeleton,
  Text,
  useToast,
  useMediaQuery,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { useMeta } from '../../system/useMeta';
import { photoUrl, useArchiveLocation, useColonies, useLocation, type Location } from '../api';
import { ColonyFormDialog } from '../components/ColonyFormDialog';
import { mapHeight } from '../mapHeights';
import { LocationFormDrawer } from '../components/LocationFormDrawer';
import { LocationYieldCard } from '../../harvest/components/YieldCards';
import { usePhotoPicker } from '../components/PhotoPicker';
import { formatAge } from '../format';
import { TasksCard } from '../../tasks/components/TasksCard';
import { ForageCard } from '../../forage/components/ForageCard';
import { WeatherCard } from '../../forage/components/WeatherCard';
import styles from './ColonyDetailPage.module.css';

export function LocationDetailPage() {
  const { locationId = '' } = useParams();
  const { t } = useTranslation();
  const location = useLocation(locationId);
  if (location.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(location.error)}
        actions={<BackLink to="/standorte" label={t('apiary.locations.title')} />}
      />
    );
  }
  if (!location.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={6} />
      </div>
    );
  }
  return <LocationDetail location={location.data} />;
}

/** A location with map, colonies and history (LOC-004). */
function LocationDetail({ location }: { location: Location }) {
  const { t } = useTranslation();
  const phone = useMediaQuery(phoneQuery);
  const compact = useMediaQuery(compactQuery);
  const toast = useToast();
  const { can } = useSession();
  const meta = useMeta();
  const colonies = useColonies(
    { location_id: location.id, status: 'active' },
    can('colonies.read'),
  );
  const archive = useArchiveLocation();
  const photo = usePhotoPicker('locations', location.id);
  const [editing, setEditing] = useState(false);
  const [newColony, setNewColony] = useState(false);
  const archived = Boolean(location.archived_at);

  // Archiving is reversible, so it happens at once with an undo (§6.10).
  const setArchived = (value: boolean) =>
    archive.mutate(
      { id: location.id, archived: value },
      {
        onSuccess: () =>
          toast.show(
            value ? t('apiary.location.archived') : t('apiary.location.unarchived'),
            'success',
            value
              ? {
                  label: t('common.undo'),
                  onClick: () => archive.mutate({ id: location.id, archived: false }),
                }
              : undefined,
          ),
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );

  return (
    <div className={styles.page}>
      {photo.input}
      <PageHeader
        back={<BackLink to="/standorte" label={t('apiary.locations.title')} />}
        title={location.name}
        meta={[
          t(`apiary.kind.${location.kind}`),
          t('apiary.locations.colonies', { count: location.active_colonies }),
          archived && location.archived_at
            ? t('apiary.location.archivedNotice', { date: formatDate(location.archived_at) })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {can('colonies.write') && !archived && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setNewColony(true)}>
                {t('apiary.location.newColony')}
              </Button>
            )}
            {can('locations.write') && (
              <MoreMenu
                label={t('common.moreActions')}
                items={[
                  { key: 'edit', label: t('common.edit'), onSelect: () => setEditing(true) },
                  {
                    key: 'photo',
                    label: location.has_photo
                      ? t('apiary.colony.photo')
                      : t('apiary.colony.addPhoto'),
                    onSelect: photo.open,
                  },
                  {
                    key: 'archive',
                    label: archived ? t('apiary.location.unarchive') : t('apiary.location.archive'),
                    separated: true,
                    onSelect: () => setArchived(!archived),
                  },
                ]}
              />
            )}
          </>
        }
      />

      <div className={styles.columns}>
        <div className={styles.main}>
          {meta.data && (
            <MapView
              tileUrl={meta.data.map_tile_url}
              attribution={meta.data.map_attribution}
              markers={[
                {
                  id: location.id,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  label: location.name,
                  badge: location.active_colonies,
                },
              ]}
              zoom={14}
              height={mapHeight('detail', { phone, compact })}
              label={t('apiary.locations.map')}
              labels={{
                zoomIn: t('apiary.map.zoomIn'),
                zoomOut: t('apiary.map.zoomOut'),
                unavailable: t('apiary.map.unavailable'),
                tilesUnavailable: t('apiary.map.tilesUnavailable'),
                fullscreen: t('apiary.map.fullscreen'),
                exitFullscreen: t('apiary.map.exitFullscreen'),
              }}
            />
          )}
          {can('colonies.read') && (
            <Section title={t('apiary.location.colonies')}>
              {colonies.isPending && <Skeleton lines={3} />}
              {colonies.data?.length === 0 && (
                <Text tone="muted" className={styles.empty}>
                  {t('apiary.location.noColonies')}
                </Text>
              )}
              <List>
                {(colonies.data ?? []).map((c) => (
                  <ListRow
                    key={c.id}
                    href={`/voelker/${c.id}`}
                    title={c.name}
                    trailing={formatAge(c.origin_date, c.origin_precision)}
                  />
                ))}
              </List>
            </Section>
          )}
          <ForageCard locationId={location.id} archived={archived} />
        </div>
        <div className={styles.side}>
          {location.has_photo && (
            <img
              className={styles.photo}
              src={photoUrl(`/locations/${location.id}/photo`, location.version)}
              alt={t('apiary.photo.alt', { name: location.name })}
            />
          )}
          <WeatherCard locationId={location.id} hideWhenUnavailable />
          <Section
            title={t('apiary.location.details')}
            footer={
              location.changed.at &&
              (location.changed.by
                ? t('apiary.colony.changed', {
                    date: formatDateTime(location.changed.at),
                    name: location.changed.by,
                  })
                : t('apiary.colony.changedNoName', { date: formatDateTime(location.changed.at) }))
            }
          >
            <KeyValue
              items={[
                {
                  key: 'address',
                  label: t('apiary.locationForm.address'),
                  value: location.address,
                },
                {
                  key: 'coordinates',
                  label: t('apiary.location.coordinates'),
                  value: (
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${location.latitude}&mlon=${location.longitude}#map=16/${location.latitude}/${location.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                    </a>
                  ),
                },
                {
                  key: 'access',
                  label: t('apiary.locationForm.access'),
                  value: location.access && <span className={styles.notes}>{location.access}</span>,
                },
                {
                  key: 'owner',
                  label: t('apiary.locationForm.ownerContact'),
                  value: location.owner_contact,
                },
                {
                  key: 'notes',
                  label: t('apiary.locationForm.notes'),
                  value: location.notes && <span className={styles.notes}>{location.notes}</span>,
                },
              ]}
            />
          </Section>
          {can('harvest.read') && <LocationYieldCard locationId={location.id} />}
          <TasksCard
            filter={{ location_id: location.id }}
            context={{ locationId: location.id, label: location.name }}
          />
        </div>
      </div>

      {editing && <LocationFormDrawer open location={location} onClose={() => setEditing(false)} />}
      {newColony && (
        <ColonyFormDialog open locationId={location.id} onClose={() => setNewColony(false)} />
      )}
    </div>
  );
}
